import { useParams } from 'react-router-dom';
import type { ReactNode } from 'react';
import { segmentToRef } from '../utils/refSegment';
import { RepoLayout } from './layout/RepoLayout';
import { RepoPage } from '../pages/repos/RepoPage';
import { FileBrowserPage } from '../pages/repos/FileBrowser';
import { FileContentPage } from '../pages/repos/FileContent';
import { CommitHistoryPage } from '../pages/repos/CommitHistory';
import { CommitDetailPage } from '../pages/repos/CommitDetail';
import { BranchListPage } from '../pages/repos/BranchList';
import { PullRequestListPage } from '../pages/repos/pulls/PullRequestListPage';
import { NewPullRequestPage } from '../pages/repos/pulls/NewPullRequestPage';
import { PullRequestDetailPage } from '../pages/repos/pulls/PullRequestDetailPage';
import { RepoSettingsPage } from '../pages/repos/RepoSettingsPage';

export interface RepoRouterProps {
    /** 作为 RepoLayout 子路由时为 true：repoPath 从 useParams().owner/.repo 取，rest 从 params['*'] 取，且不包裹 RepoLayout */
    fromLayout?: boolean;
}

function wrapRepo(repoPath: string, child: ReactNode) {
    return <RepoLayout repoPath={repoPath}>{child}</RepoLayout>;
}


export function RepoRouter({ fromLayout }: RepoRouterProps = {}) {
    const params = useParams<{ owner?: string; repo?: string; '*'?: string }>();
    const fullPath = params['*'] || '';
    const owner = params.owner;
    const repo = params.repo;

    const repoPathFromParams = owner && repo ? `${owner}/${repo}` : '';
    const sepIndex = fromLayout ? -1 : fullPath.indexOf('/-/');

    const repoPath = sepIndex >= 0
        ? fullPath.substring(0, sepIndex)
        : repoPathFromParams || fullPath.trim();
    const rest = sepIndex >= 0
        ? fullPath.substring(sepIndex + 3)
        : (fromLayout ? fullPath : '');

    if (fromLayout) {
        return renderContent(repoPath, rest, false);
    }

    if (sepIndex === -1) {
        if (!repoPath) return <RepoPage />;
        return wrapRepo(repoPath, <RepoPage />);
    }

    return renderContent(repoPath, rest, true);
}

function renderContent(repoPath: string, rest: string, wrap: boolean): ReactNode {

    const W = wrap ? wrapRepo : (_p: string, c: ReactNode) => c;

    if (rest.startsWith('tree/')) {
        const subPath = rest.substring(5);
        if (!subPath) return W(repoPath, <FileBrowserPage repoPath={repoPath} />);
        const treeMatch = subPath.match(/^(refs\/heads\/.+|refs\/tags\/.+|[^/]+)(?:\/(.*))?$/);
        const refRaw = treeMatch ? treeMatch[1] : subPath.split('/')[0];
        const ref = refRaw ? segmentToRef(refRaw) : '';
        const path = treeMatch && treeMatch[2] !== undefined ? treeMatch[2] : subPath.includes('/') ? subPath.replace(/^[^/]+\//, '') : '';
        return W(repoPath, <FileBrowserPage repoPath={repoPath} gitRef={ref} path={path} />);
    }

    if (rest.startsWith('commits/')) {
        const refSeg = rest.substring(8);
        const ref = refSeg ? segmentToRef(refSeg) : '';
        return W(repoPath, <CommitHistoryPage repoPath={repoPath} refName={ref} />);
    }

    if (rest.startsWith('commit/')) {
        const sha = rest.substring(7);
        return W(repoPath, <CommitDetailPage repoPath={repoPath} sha={sha} />);
    }

    if (rest.startsWith('blob/')) {
        const subPath = rest.substring(5);
        const blobMatch = subPath.match(/^(refs\/heads\/.+|refs\/tags\/.+|[^/]+)(?:\/(.*))?$/);
        const refRaw = blobMatch ? blobMatch[1] : subPath.split('/')[0];
        const ref = refRaw ? segmentToRef(refRaw) : '';
        const filePath = blobMatch && blobMatch[2] !== undefined ? blobMatch[2] : subPath.includes('/') ? subPath.replace(/^[^/]+\//, '') : '';
        return W(repoPath, <FileContentPage repoPath={repoPath} gitRef={ref} filePath={filePath} />);
    }

    if (rest === 'branches' || rest.startsWith('branches/')) {
        return W(repoPath, <BranchListPage />);
    }
    if (rest === 'pulls') {
        return W(repoPath, <PullRequestListPage />);
    }
    if (rest === 'pulls/new') {
        return W(repoPath, <NewPullRequestPage />);
    }
    if (rest.startsWith('pulls/')) {
        return W(repoPath, <PullRequestDetailPage />);
    }
    if (rest === 'settings') {
        return W(repoPath, <RepoSettingsPage />);
    }

    return W(repoPath, <RepoPage />);
}

